import React from 'react';

interface Step {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | React.ReactNode;
  number: number;
  activeColor?: string;
  activeTextColor?: string;
  iconColor?: string;
}

interface StepIndicatorProps {
  currentStep: number;
  steps: Step[];
  progressColor?: string;
}

export function StepIndicator({ currentStep, steps, progressColor = 'bg-green-bb-2' }: StepIndicatorProps) {
  return (
    <div
      className="flex justify-center mb-6 sm:mb-7 lg:mb-8 px-2 sm:px-0"
      role="group"
      aria-label={`Progresso: etapa ${currentStep} de ${steps.length}`}
    >
      <div className="relative w-full max-w-sm sm:max-w-md md:max-w-lg">
        {/* Progress Line Background */}
        <div
          className="absolute h-0.5 bg-gray-bb-4"
          style={{ top: 'calc(100% - 14px)', left: '10%', right: '10%' }}
          aria-hidden="true"
        />

        {/* Progress Line Active */}
        <div
          className={`absolute h-0.5 ${progressColor} transition-all duration-500`}
          style={{
            top: 'calc(100% - 14px)',
            left: '10%',
            // A linha vai do 1º ao último círculo (80% da largura).
            width: `${(80 * (Math.min(currentStep, steps.length) - 1)) / Math.max(steps.length - 1, 1)}%`,
          }}
          aria-hidden="true"
        />

        {/* Steps Container */}
        <div className="flex justify-between relative z-10">
          {steps.map((step) => {
            const isActive = currentStep >= step.number;
            const isCurrent = currentStep === step.number;
            const iconColorClass = isActive && step.iconColor ? step.iconColor : 'text-gray-300';

            // Check if icon is a React component (SVG) or a ReactNode (react-icons)
            const isSvgComponent = typeof step.icon === 'function';

            return (
              <div
                key={step.number}
                className="flex flex-col items-center"
                aria-label={`Etapa ${step.number}`}
                aria-current={isCurrent ? "step" : undefined}
              >
                <div className="mb-2 sm:mb-3" aria-hidden="true">
                  {isSvgComponent ? (
                    (() => {
                      const IconComponent = step.icon as React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
                      return (
                        <IconComponent
                          className={`w-8 h-8 sm:w-10 sm:h-10 md:w-16 md:h-16 transition-all duration-300 ${iconColorClass}`}
                        />
                      );
                    })()
                  ) : (
                    <span className={`text-3xl sm:text-4xl md:text-5xl transition-all duration-300 ${iconColorClass}`}>
                      {step.icon as React.ReactNode}
                    </span>
                  )}
                </div>
                <div
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-sm sm:text-base transition-all duration-300 ${
                    isActive
                      ? `${step.activeColor || 'bg-green-bb-2'} ${step.activeTextColor || 'text-white-bb'}`
                      : 'bg-gray-bb-4 text-gray-bb-2'
                  }`}
                  aria-hidden="true"
                >
                  {step.number}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
